import Card from "@/UI/Card";
import { Upload, Button, Table, Input, Modal, message, Select, Form } from "antd";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { addSingleAttachment, deleteAttachment } from "@/redux/rtk/features/CRM/attachment/attachmentSlice";
import usePermissions from "@/utils/usePermissions";
import { toastHandler, getAttachmentUrl } from "@/utils/functions";
import { DeleteOutlined, EyeOutlined, UploadOutlined, PlusOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import axios from "axios";

const { Dragger } = Upload;

export default function StudentAttachments({
  student,
  onUpdate,
  studentType = 'deal', // 'deal', 'lead', or 'contact'
  studentId,
  leadId
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { permissions } = usePermissions();
  const canEdit = permissions?.includes("create-attachment");
  const [form] = Form.useForm();

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // Load existing attachments
  useEffect(() => {
    if (student) {
      let studentAttachments = [];

      // Get attachments based on student type
      if (studentType === 'deal' && student.attachments && Array.isArray(student.attachments)) {
        // For deals, attachments already include lead attachments from backend
        studentAttachments = student.attachments;
      } else if (studentType === 'lead' && student.attachment && Array.isArray(student.attachment)) {
        studentAttachments = student.attachment;
      } else if (studentType === 'contact' && student.attachment && Array.isArray(student.attachment)) {
        studentAttachments = student.attachment;
      }

      // Filter out old attachment types (passport_1, passport_2, id_1, id_2)
      // Only show attachments that don't have these types or have custom names
      const filteredAttachments = studentAttachments.filter(att => {
        if (!att) return false;
        const type = att.attachmentType;
        return !type || !['passport_1', 'passport_2', 'id_1', 'id_2'].includes(type);
      });

      setAttachments(filteredAttachments || []);
    } else {
      setAttachments([]);
    }
  }, [student, studentType]);

  const handleUpload = async (values) => {
    const attachmentsToUpload = values.attachments || [];
    const validAttachments = attachmentsToUpload.filter(item => item.file && item.file.length > 0);

    if (validAttachments.length === 0) {
      message.error(t("attachment.fileRequired"));
      return;
    }

    setUploading(true);

    try {
      const uploadPromises = validAttachments.map(async (item) => {
        const file = item.file[0].originFileObj;
        
        // Check file size
        const maxSize = 50 * 1024 * 1024; // 50MB
        if (file.size > maxSize) {
          throw new Error(`${file.name}: ${t("attachment.fileSizeError")}`);
        }

        const finalName = item.name === "other" ? item.customName : item.name;
        
        const formData = new FormData();
        formData.append('images[]', file);
        formData.append('attachmentName', finalName.trim());

        // Add appropriate ID based on student type
        if (studentType === 'deal') {
          formData.append('dealId', studentId || student?.id || '');
          if (leadId || student?.leadId) {
            formData.append('leadId', leadId || student?.leadId || '');
          }
        } else if (studentType === 'lead') {
          formData.append('leadId', studentId || student?.id || '');
        } else if (studentType === 'contact') {
          formData.append('contactId', studentId || student?.id || '');
        }

        const result = await dispatch(addSingleAttachment(formData));
        if (result.payload?.id || result.payload?.message === "success") {
          return result.payload;
        } else if (result.payload?.error) {
          throw new Error(result.payload.error);
        } else {
          throw new Error(t("errors.somethingWentWrong"));
        }
      });

      await Promise.all(uploadPromises);
      
      message.success(t("attachment.uploadSuccess"));
      setUploadModalVisible(false);
      form.resetFields();

      // Refresh data
      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error("Upload error:", error);
      message.error(error.message || t("errors.somethingWentWrong"));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId) => {
    if (!attachmentId) return;

    Modal.confirm({
      title: t("attachment.confirmDelete"),
      icon: <DeleteOutlined />,
      okText: t("common.yes"),
      okType: 'danger',
      cancelText: t("common.no"),
      onOk: async () => {
        setLoading(true);
        try {
          const result = await dispatch(deleteAttachment(attachmentId));

          if (result.payload?.message === "success") {
            const responseData = result.payload?.data || {};
            const successMessage = responseData?.message || t("attachment.deleteSuccess");
            toastHandler(successMessage, "success");
            setAttachments(prev => prev.filter(att => att.id !== attachmentId));
            if (onUpdate) {
              onUpdate();
            }
          } else if (result.payload?.error) {
            toastHandler(result.payload.error, "error");
          }
        } catch (error) {
          console.error("Delete error:", error);
        } finally {
          setLoading(false);
        }
      },
    });

  };

  const handleView = (attachmentPath, attachmentName) => {
    const url = getAttachmentUrl(attachmentPath);
    setPreviewImage({
      url,
      name: attachmentName || t("attachment.viewArea"),
      path: attachmentPath
    });
    setPreviewModalVisible(true);
  };

  const columns = [
    {
      title: t("attachment.name"),
      dataIndex: 'attachmentName',
      key: 'attachmentName',
      render: (text) => text || t("attachment.noName"),
    },
    {
      title: t("attachment.type"),
      key: 'type',
      render: (_, record) => {
        const path = record.attachmentPath || '';
        const extension = path.split('.').pop()?.toLowerCase() || '';
        return extension.toUpperCase() || '-';
      },
    },
    {
      title: t("common.actions"),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div className="flex items-center justify-center gap-2">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record.attachmentPath, record.attachmentName)}
            title={t("common.view")}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          />
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => {
              const url = getAttachmentUrl(record.attachmentPath);
              const link = document.createElement("a");
              link.href = url;
              link.target = "_blank";
              link.download = record.attachmentName || "attachment";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            title={t("attachment.download")}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          />
          {canEdit && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
              loading={loading}
              title={t("common.delete")}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          )}
        </div>
      ),
    },
  ];



  return (
    <Card className="border border-gray-200 dark:border-gray-700">
      <div className="p-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            {t("lead.attachments")}
          </h4>
          {canEdit && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              {t("attachment.add")}
            </Button>
          )}
        </div>

        <Table
          columns={columns}
          dataSource={attachments}
          rowKey="id"
          pagination={attachments.length > 10 ? { pageSize: 10 } : false}
          locale={{
            emptyText: t("attachment.empty"),
          }}
        />

        {canEdit && (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={() => setUploadModalVisible(true)}
              className="flex items-center justify-center gap-2 w-full py-3 px-4 rounded-xl border-2 border-dashed border-primary/30 text-primary hover:border-primary hover:bg-primary/5 transition-all font-medium"
            >
              <PlusOutlined /> {t("attachment.add")}
            </button>
          </div>
        )}

        <Modal
          title={t("attachment.addNew")}
          open={uploadModalVisible}
          onOk={() => form.submit()}
          onCancel={() => {
            setUploadModalVisible(false);
            form.resetFields();
          }}
          destroyOnClose
          confirmLoading={uploading}
          okText={t("attachment.upload")}
          cancelText={t("common.cancel")}
          width={800}
        >
          <Form form={form} onFinish={handleUpload} layout="vertical">
            <Form.List name="attachments" initialValue={[{}]}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <div key={key} className="flex gap-4 items-start mb-4 border p-4 rounded bg-gray-50 dark:bg-gray-800 dark:border-gray-700 relative">
                      <Form.Item
                        {...restField}
                        name={[name, 'name']}
                        label={t("application.attachmentTitle")}
                        rules={[{ required: true, message: t("application.missingTitle") }]}
                        className="flex-1 mb-0"
                      >
                        <Select placeholder={t("application.attachmentTitlePlaceholder")}>
                          {[
                            "passport",
                            "high_school_transcript",
                            "bachelor_degree_certificate",
                            "bachelor_degree_transcript",
                            "course_description",
                            "master_degree_certificate",
                            "master_degree_transcript",
                            "english_proficient_certificate",
                            "conditional_acceptance_letter",
                            "final_letter",
                            "ticket",
                            "payment",
                            "conditional_acceptance",
                            "student_letter",
                            "other"
                          ].map((type) => (
                            <Select.Option key={type} value={type === 'other' ? 'other' : t(`attachment.${type}`)}>
                              {t(`attachment.${type}`)}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, curValues) =>
                          prevValues.attachments?.[name]?.name !== curValues.attachments?.[name]?.name
                        }
                      >
                        {({ getFieldValue }) =>
                          getFieldValue(['attachments', name, 'name']) === 'other' ? (
                            <Form.Item
                              {...restField}
                              name={[name, 'customName']}
                              label={t("attachment.namePlaceholder")}
                              rules={[{ required: true, message: t("attachment.nameRequired") }]}
                              className="flex-1 mb-0"
                            >
                              <Input placeholder={t("attachment.namePlaceholder")} />
                            </Form.Item>
                          ) : null
                        }
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'file']}
                        rules={[{ required: true, message: t("application.missingFile") }]}
                        label={t("application.file")}
                        className="flex-1 mb-0"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => {
                          if (Array.isArray(e)) return e;
                          return e?.fileList;
                        }}
                      >
                        <Upload borderUpload={() => false} maxCount={1}>
                          <Button icon={<UploadOutlined />}>{t("application.selectFile")}</Button>
                        </Upload>
                      </Form.Item>
                      {fields.length > 1 && (
                        <Button
                          type="text"
                          danger
                          onClick={() => remove(name)}
                          className="mt-8"
                          icon={<DeleteOutlined />}
                        />
                      )}
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      {t("application.addAttachment")}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form>
        </Modal>

        {/* Preview Modal */}
        <Modal
          title={previewImage?.name || t("attachment.viewArea")}
          open={previewModalVisible}
          onCancel={() => {
            setPreviewModalVisible(false);
            setPreviewImage(null);
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setPreviewModalVisible(false);
                setPreviewImage(null);
              }}
            >
              {t("common.close")}
            </Button>,
            <Button
              key="download"
              type="primary"
              onClick={() => {
                if (previewImage?.url) {
                  window.open(previewImage.url, '_blank');
                }
              }}
            >
              {t("attachment.openInNewWindow")}
            </Button>
          ]}
          width={800}
          centered
        >
          <div className="flex justify-center items-center min-h-[400px] bg-gray-100 dark:bg-gray-800 rounded-lg">
            {previewImage?.url && (
              <div className="w-full">
                {previewImage.path?.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={previewImage.url}
                    className="w-full h-[600px] border-0 rounded"
                    title={previewImage.name}
                  />
                ) : (
                  <img
                    src={previewImage.url}
                    alt={previewImage.name}
                    className="max-w-full max-h-[600px] mx-auto object-contain rounded"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                )}
                <div
                  className="hidden items-center justify-center h-[600px] text-gray-500"
                  style={{ display: 'none' }}
                >
                  <div className="text-center">
                    <p className="text-lg mb-2">{t("attachment.loadFailed")}</p>
                    <Button
                      type="link"
                      onClick={() => window.open(previewImage.url, '_blank')}
                    >
                      {t("attachment.openInNewWindow")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      </div>
    </Card>
  );
}

